// Logged-out homepage (app/views/home/marketing). Tabs, explorers, the carousel and the nav are
// Bootstrap components; this adds the hero rotation, cross-links into the booking tabs, the
// Calendly scheduler, the free-sample preview and the lead forms.

const $ = (id) => document.getElementById(id);
const all = (sel, root = document) => Array.from(root.querySelectorAll(sel));
const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const esc = (x) => String(x).replace(/&/g, "&amp;").replace(/</g, "&lt;");
const showTab = (button) => window.bootstrap.Tab.getOrCreateInstance(button).show();

function initNav() {
  const nav = document.querySelector(".wdm-nav");
  const onScroll = () => nav.classList.toggle("scrolled", window.scrollY > 8);
  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();

  // Phone menu: close it first, then go to the link's section or open its modal
  const menu = $("wdmMenu");
  all("a", menu).forEach((a) =>
    a.addEventListener("click", (e) => {
      e.preventDefault();
      menu.addEventListener(
        "hidden.bs.offcanvas",
        () => {
          if (a.dataset.modal) window.bootstrap.Modal.getOrCreateInstance(document.querySelector(a.dataset.modal)).show();
          else window.location.hash = a.getAttribute("href");
        },
        { once: true }
      );
      window.bootstrap.Offcanvas.getOrCreateInstance(menu).hide();
    })
  );
}

// Hero sector tabs: rotate until the visitor picks one
function initRoleExplorer() {
  const buttons = all("#rxRoles [data-bs-toggle='tab']");
  let timer = null;
  const stop = () => clearInterval(timer);

  buttons.forEach((b) =>
    b.addEventListener("click", (e) => {
      if (e.isTrusted) stop();
    })
  );

  if (!reducedMotion()) {
    let idx = 0;
    timer = setInterval(() => {
      idx = (idx + 1) % buttons.length;
      showTab(buttons[idx]);
    }, 6000);
    $("rx").addEventListener("mouseenter", stop);
    $("rx").addEventListener("focusin", stop);
  }
  return stop;
}

function setMode(mode) {
  showTab($(mode === "ask" ? "tabAsk" : "tabDemo"));
}

function initCrossLinks(stopRotation) {
  document.addEventListener("click", (e) => {
    // Hero forecast links open that tab of the methodology section
    const fx = e.target.closest("[data-fx]");
    if (fx) {
      stopRotation();
      showTab($(`fx-tab-${fx.dataset.fx}`));
    }

    const mode = e.target.closest("[data-mode]");
    if (mode) {
      stopRotation();
      setMode(mode.dataset.mode);
      // e.g. "Request a demo" in the latest-insights pop-up
      const modal = mode.closest(".modal");
      if (modal) window.bootstrap.Modal.getInstance(modal)?.hide();
    }

    // "I agree" in a data-terms pop-up ticks the form's box
    const agree = e.target.closest("[data-agree]");
    if (agree) $(agree.dataset.agree).checked = true;
  });
}

// On phones the industry detail card sits below the tiles, so bring it into view when a tile is picked
function initIndustries() {
  all("#industries [data-bs-toggle='tab']").forEach((tile) =>
    tile.addEventListener("shown.bs.tab", () => {
      if (window.innerWidth >= 992) return;
      document.querySelector(tile.dataset.bsTarget).scrollIntoView({ behavior: reducedMotion() ? "auto" : "smooth" });
    })
  );
}

function initSamplePreview() {
  const columns = JSON.parse(document.querySelector(".wdm-preview").dataset.columns);
  function preview() {
    const country = $("dsCountry").value || "Saudi Arabia";
    const dataset = $("dsSeg").value || "Treatment plants";
    const cols = columns[dataset];
    $("dsPT").textContent = `${country} · ${dataset}`;
    $("dsHead").innerHTML = cols.map((x) => `<th scope="col">${esc(x)}</th>`).join("");
    let rows = "";
    for (let r = 0; r < 6; r++) {
      rows += `<tr>${cols.map((x, j) => `<td><i style="width:${40 + ((r * 37 + j * 23) % 55)}%"></i></td>`).join("")}</tr>`;
    }
    $("dsBody").innerHTML = rows;
  }
  $("dsCountry").addEventListener("change", preview);
  $("dsSeg").addEventListener("change", preview);
  preview();
}

// ---- Demo booking -----------------------------------------------------------

let calendlyLoad = null;
function loadCalendly() {
  if (window.Calendly) return Promise.resolve();
  if (!calendlyLoad) {
    calendlyLoad = new Promise((resolve, reject) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://assets.calendly.com/assets/external/widget.css";
      document.head.appendChild(link);
      const s = document.createElement("script");
      s.src = "https://assets.calendly.com/assets/external/widget.js";
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return calendlyLoad;
}

// The scheduler sits at the foot of the page, so only fetch Calendly once the visitor gets near it
function initCalendly() {
  const cal = $("calendly");
  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      // On failure the "Open the booking page" link below the scheduler still works
      loadCalendly()
        .then(() => window.Calendly.initInlineWidget({ url: cal.dataset.url, parentElement: cal }))
        .catch(() => (cal.hidden = true));
    },
    { rootMargin: "600px" }
  );
  observer.observe(cal);
}

// ---- Lead forms -------------------------------------------------------------

// used: what to show when the company has already had its one request of this kind
const ERRORS = {
  analyst_question: { el: "errA", used: "askUsed", msg: "Please add your name, a valid work email, your company and your question, and agree to the data terms." },
  sample: { el: "dsErr", used: "dsUsed", msg: "Please choose a country and dataset, add your name, work email and company, and agree to the data terms." },
  brochure: { el: "broNote", msg: "Please fill in all three fields, using a valid email address." },
};

function formData(form, kind) {
  const data = { kind };
  new FormData(form).forEach((value, key) => {
    data[key] = value.trim();
  });
  return data;
}

function onSuccess(kind, form, data) {
  switch (kind) {
    case "analyst_question":
      form.hidden = true;
      $("askDone").hidden = false;
      break;
    case "sample":
      $("dsWhat").textContent = `${data.country} · ${data.dataset}`;
      $("dsTo").textContent = data.email;
      form.hidden = true;
      $("dsDone").hidden = false;
      break;
    case "brochure":
      form.hidden = true;
      $("broNote").hidden = true;
      $("broDone").hidden = false;
      break;
  }
}

function initLeadForms() {
  const url = document.querySelector(".wdm-page").dataset.leadUrl;
  const token = document.querySelector("meta[name='csrf-token']")?.content;

  all(".js-lead-form").forEach((form) =>
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      // Not form.dataset: the sample form's <select name="dataset"> shadows that property
      const kind = form.getAttribute("data-kind");
      const err = $(ERRORS[kind].el);
      if (!form.checkValidity()) {
        // Bootstrap's was-validated outlines each missing field in red
        form.classList.add("was-validated");
        form.querySelector(":invalid")?.focus();
        err.textContent = ERRORS[kind].msg;
        return;
      }
      form.classList.remove("was-validated");
      const data = formData(form, kind);

      const button = form.querySelector("button[type='submit']");
      const label = button.textContent;
      button.disabled = true;
      button.textContent = "Sending…";
      err.textContent = "";
      try {
        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": token },
          body: JSON.stringify(data),
        });
        if (res.ok) {
          onSuccess(kind, form, data);
        } else if (res.status === 409) {
          form.hidden = true;
          $(ERRORS[kind].used).hidden = false;
        } else if (res.status === 429) {
          err.textContent = "Too many requests from your network. Please try again in an hour.";
        } else {
          const body = await res.json().catch(() => ({}));
          err.textContent = body.error || "Something went wrong. Please try again.";
        }
      } catch (ex) {
        err.textContent = "Something went wrong. Please try again.";
      } finally {
        button.disabled = false;
        button.textContent = label;
      }
    })
  );
}

// Visitors bounced from login (expired / no subscription) land here with the reason in a modal.
// Its markup still carries Bootstrap 3's "in" class, which Bootstrap 5 ignores, so open it properly.
function showAccessDenied() {
  const modal = $("accessDeniedModal");
  if (!modal) return;
  modal.classList.remove("in");
  window.bootstrap.Modal.getOrCreateInstance(modal).show();
}

function initTracking() {
  document.addEventListener("click", (e) => {
    const el = e.target.closest("[data-cta]");
    if (el && typeof window.gtag === "function") window.gtag("event", "cta_click", { cta: el.dataset.cta });
  });
}

function init() {
  const page = document.querySelector(".wdm-page");
  if (!page || page.dataset.ready) return;
  page.dataset.ready = "1";

  showAccessDenied();
  initNav();
  const stopRotation = initRoleExplorer();
  initCrossLinks(stopRotation);
  initIndustries();
  initSamplePreview();
  initCalendly();
  initLeadForms();
  initTracking();
}

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
else init();
